import Notification from '@components/common/Notification';
import DatepickerInput from '@components/form/DatepickerInput';
import { TextInputDefault } from '@components/form/TextInput';
import { RootState } from '@redux/store';
import { ChangeEventHandler, FormEvent, useCallback, useEffect, useState } from 'react'
import { NotificationType } from 'react-app-env';
import { useSelector } from 'react-redux';
import { DateValueType } from 'react-tailwindcss-datepicker';
import { FaTrashAlt } from 'react-icons/fa';
import axios, { AxiosResponse } from 'axios';
import { API_URL } from '@redux/api';
import SelectInput from '@components/form/SelectInput';
import { FiPlus } from 'react-icons/fi';
import TimePickerInput from '@components/form/TimePickerInput';
import dayjs from 'dayjs';
import { validateSubmitCreateLoading } from '@redux/validations/loadings';
import Loading from '@components/common/Loading';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

type PicType = {
  name: string;
  phone: string;
};

type InventoryItem = {
  item: any;
  quantity: number | undefined;
  unit: string;
  trip: number;
};

type InventoriesStateType = InventoryItem[] | [];

type LineFormError = {
  name?: string | undefined;
  quantity?: string | undefined;
  unit?: string | undefined;
};

export type ErrorsState = {
  eventDate?: string;
  eventLocation?: string;
  picName?: string;
  picPhone?: string;
  loadingDate?: string;
  loadingTime?: string;
  coordinatorName?: string;
  coordinatorPhone?: string;
  inventories?: LineFormError[] | [];
};

export type SubmitCreateLoadingPayload = {
  eventDate: string | null;
  eventLocation: string;
  pic: PicType,
  loadingDate: string | null;
  loadingTime: string;
  type: string;
  coordinator: PicType;
  adminCreator: string;
  inventories: InventoriesStateType
}

const CreateLoadingScheduleForm = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [eventDate, setEventDate] = useState<DateValueType | null>(null);
  const [eventLocation, setEventLocation] = useState("");
  const [pic, setPic] = useState<PicType>({
    name: "",
    phone: ""
  });
  const [loadingDate, setLoadingDate] = useState<DateValueType | null>(null);
  const [loadingTime, setLoadingTime] = useState<string>("");
  // const [type, setType] = useState("6748657767fcd44bdc1a44be");
  const [coordinatorContact, setCoordinatorContact] = useState<PicType>({
    name: "",
    phone: ""
  });
  const [inventories, setInventories] = useState<InventoriesStateType>([]);

  const [inventoriesOptions, setInventoriesOptions] = useState<any>([]);

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorsState>({});
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const fetchInventoriesOptions = useCallback(async () => {
    setFetchLoading(true);
    const response: AxiosResponse = await axios.get(`${API_URL}/inventory/options`);
    const { status } = response;
    if (status === 200) {
      const { data } = response.data;

      setInventoriesOptions(data);
      setFetchLoading(false);
    } else {
      setFetchLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setErrors({});
    setNotification(null);
    fetchInventoriesOptions();

    let defaultErrors: ErrorsState = {};

    const defaultInventoryErrors = [];
    const defaultInventories = [];

    if (inventories.length === 0) {
      defaultInventories.push({
        item: null,
        quantity: undefined,
        unit: "",
        trip: 0
      });

      defaultInventoryErrors.push({
        name: undefined,
        quantity: undefined,
        unit: undefined
      });
    };

    defaultErrors["inventories"] = defaultInventoryErrors;

    setInventories(defaultInventories);
    setErrors(defaultErrors);

    return () => { }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddNewLineForm = (e: any) => {
    e.preventDefault();
    const inventoriesValue = [...inventories, {
      item: null,
      quantity: undefined,
      unit: "",
      trip: 0
    }];

    const errorsValue = {...errors};
    if(errors.inventories) {
      errorsValue["inventories"] = [...errors.inventories, {
        name: undefined,
        quantity: undefined,
        unit: undefined
      }];
    };

    setInventories(inventoriesValue);
    setErrors(errorsValue);
  };

  const onRemoveLineForm = (e: any, idx: number) => {
    e.preventDefault();
    const newInventories = [...inventories];
    newInventories.splice(idx, 1);

    let newErrors = {...errors};
    let inventoryErrors: LineFormError[] = [];
    
    if(errors.inventories) {
      inventoryErrors = [...errors.inventories];
      inventoryErrors.splice(idx, 1);
    };

    newErrors["inventories"] = inventoryErrors;

    setInventories(newInventories);
    setErrors(newErrors);
  };

  const onChangeError = ({ target, value, idx }: {
    target: "name" | "quantity";
    value: string | undefined;
    idx: number;
  }) => {
    let newErrors = {...errors};
    let inventoryErrors = [];

    if(errors.inventories) {
      inventoryErrors = errors.inventories.map((item, sidx) => {
        if (idx === sidx) {
          return {
            ...item,
            [target]: value
          }
        };
  
        return item;
      });

      newErrors["inventories"] = inventoryErrors;
    };

    setErrors(newErrors);
  };

  const onChangeInput = (value: any, idx: number, targetName: string) => {
    onChangeError({
      target: targetName === 'item' ? "name" : "quantity",
      value: undefined,
      idx
    });

    const newInventories = inventories.map((inventory, sidx) => {
      if (idx !== sidx) return inventory;
      if (targetName === 'item') {
        return {
          ...inventory,
          unit: value.unit,
          [targetName]: value
        }
      };

      return {
        ...inventory,
        [targetName]: value
      }
    });

    setInventories(newInventories);
  };

  const handleTimeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const time = e.target.value;
    setLoadingTime(time);
  };

  const isValid = (payload: SubmitCreateLoadingPayload) => {
    const { errors, isValid } = validateSubmitCreateLoading(payload);
    if (errors) setErrors(errors);

    console.log("isValid", isValid);
    console.log(errors);

    return isValid;
  }

  const onSubmit = async(e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitLoading(true);
    setNotification(null);
    setErrors({});

    const inventoriesPayload = inventories.map(data => {
      return {
        ...data,
        quantity: Number(data.quantity)
      };
    });

    const payload = {
      eventDate: eventDate ? dayjs(eventDate?.startDate).format("DD/MM/YYYY") : null,
      eventLocation,
      pic,
      loadingDate: loadingDate ? dayjs(loadingDate?.startDate).format("DD/MM/YYYY") : null,
      loadingTime,
      type: "6748657767fcd44bdc1a44be",
      coordinator: coordinatorContact,
      adminCreator: user._id,
      inventories: inventoriesPayload,
    };

    if (isValid(payload)) {
      const submitPayload = {
        ...payload,
        inventories: inventoriesPayload.map(data => {
          return {
            ...data,
            item: data.item.value
          }
        })
      };
      
      const response: AxiosResponse = await axios.post(`${API_URL}/loading/create`, submitPayload);
      if(response.status === 200) {
        setNotification({
          isError: false,
          title: "Sukses !",
          text: "Data Loading IN berhasil tersimpan"
        });

        setTimeout(() => {
          setSubmitLoading(false);
          navigate("/jadwal-loading/in");
        }, 2000);
      } else {
        setSubmitLoading(false);
        setNotification({
          isError: true,
          title: "Terjadi kesalahan",
          text: "Mohon lengkapi form di atas dengan benar"
        });
      };
    } else {
      setSubmitLoading(false);
      setNotification({
        isError: true,
        title: "Terjadi kesalahan",
        text: "Mohon lengkapi form di atas dengan benar"
      });
    };
  };

  return (
    <div>
      <Notification notification={notification} className='mb-10'/>
      {fetchLoading ? 
        <div className='bg-white py-16 rounded-xl flex items-center justify-center'>
          <Loading loading={fetchLoading} type='beat-loader' size={10} parentClass='text-sm' />
        </div>
        : <form onSubmit={onSubmit}>
          {/* INFORMASI JADWAL ACARA */}
          <div className='bg-white p-4 rounded-xl'>
            <div>
              <h2 className='font-semibold text-center md:text-left'>Informasi Jadwal Acara</h2>
            </div>
            <hr className='my-4' />
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <DatepickerInput
                  label="Tanggal Acara"
                  error={errors.eventDate}
                  disabled={false}
                  name='eventDate'
                  value={eventDate}
                  className=''
                  onChange={(value) => setEventDate(value)}
                />
              </div>
              <div>
                <TextInputDefault
                  name="eventLocation"
                  label="Lokasi Acara"
                  value={eventLocation}
                  placeholder="Masukkan nama gedung / venue"
                  onChange={({ target }) => {
                    const { value } = target;
                    setEventLocation(value);
                  }}
                  disabled={false}
                  error={errors.eventLocation}
                />
              </div>
              <div>
                <TextInputDefault
                  name="picName"
                  label="Nama PIC"
                  value={pic.name}
                  placeholder="Masukkan nama PIC Gedung"
                  onChange={({ target }) => {
                    const { value } = target;
                    setPic({
                      ...pic,
                      name: value
                    });
                  }}
                  disabled={false}
                  error={errors.picName}
                />
              </div>
              <div>
                <TextInputDefault
                  name="picPhone"
                  label="No. HP PIC"
                  value={pic.phone}
                  type='number'
                  placeholder="Masukkan no. hp PIC Gedung"
                  onChange={({ target }) => {
                    const { value } = target;
                    setPic({
                      ...pic,
                      phone: value
                    });
                  }}
                  disabled={false}
                  error={errors.picPhone}
                />
              </div>
            </div>
          </div>

          {/* INFORMASI JADWAL LOADING IN */}
          <div className='bg-white p-4 rounded-xl mt-5'>
            <div>
              <h2 className='font-semibold text-center md:text-left'>Informasi Jadwal Loading In</h2>
            </div>
            <hr className='my-4' />
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <DatepickerInput
                  label="Tanggal Loading IN"
                  error={errors.loadingDate}
                  disabled={false}
                  name='loadingDate'
                  value={loadingDate}
                  className=''
                  onChange={(value) => setLoadingDate(value)}
                />
              </div>
              <div>
                <TimePickerInput
                  name="loadingTime"
                  label="Jam Loading IN"
                  value={loadingTime}
                  placeholder="contoh: 22.00 WIB"
                  disabled={false}
                  error={errors.loadingTime || null}
                  onChange={handleTimeChange}
                />
              </div>
              <div>
                <TextInputDefault
                  name="coordinatorName"
                  label="Nama Koordinator"
                  value={coordinatorContact.name}
                  placeholder="Masukkan nama Koordinator"
                  onChange={({ target }) => {
                    const { value } = target;
                    setCoordinatorContact({
                      ...coordinatorContact,
                      name: value
                    });
                  }}
                  disabled={false}
                  error={errors.coordinatorName}
                />
              </div>
              <div>
                <TextInputDefault
                  name="coordinatorPhone"
                  label="No. HP Koordinator"
                  value={coordinatorContact.phone}
                  type='number'
                  placeholder="Masukkan no. hp Koordinator"
                  onChange={({ target }) => {
                    const { value } = target;
                    setCoordinatorContact({
                      ...coordinatorContact,
                      phone: value
                    });
                  }}
                  disabled={false}
                  error={errors.coordinatorPhone}
                />
              </div>
            </div>
          </div>

          {/* INFORMASI BARANG LOADING */}
          <div className='bg-white p-4 rounded-xl mt-5'>
            <div>
              <h2 className='font-semibold text-center md:text-left'>Data Barang Loading</h2>
            </div>
            <hr className='my-4' />
            {inventories.map((item, idx) => {
              return (
                <div key={`new-inventory-${idx}`} className="mt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold pb-1">Nama barang</label>
                      <SelectInput
                        isMulti={false}
                        label=""
                        placeholder="Pilih barang"
                        disabled={submitLoading}
                        options={inventoriesOptions}
                        value={item.item}
                        isSearchable={true}
                        error={errors.inventories ? errors.inventories[idx]?.name : null}
                        onChange={(value) => {
                          onChangeInput(value, idx, "item");
                        }}
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-semibold pb-1">Qty {item.unit && `(${item.unit})`}</label>
                      <div className="flex items-center justify-center gap-3">
                        <TextInputDefault
                          name={`quantity_${idx}`}
                          label=""
                          type="number"
                          value={item.quantity}
                          placeholder="Jumlah"
                          onChange={({ target }) => {
                            const { value } = target;
                            onChangeInput(value, idx, "quantity");
                          }}
                          disabled={submitLoading}
                          error={errors.inventories ? errors.inventories[idx]?.quantity : null}
                        />
                        <button className="text-red-500" onClick={(e) => onRemoveLineForm(e, idx)}>
                          <FaTrashAlt className="" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            <button
              className="bg-sky-500 text-white rounded-xl py-3 flex items-center justify-center w-full mt-4"
              onClick={onAddNewLineForm}
            >
              <FiPlus />
              Tambah barang lain
            </button>
          </div>

          <div className='mt-4'>
            <Notification notification={notification} />
          </div>

          <div className='mt-12'>
            <button
              type="submit"
              className="bg-orange-600 text-white rounded-xl py-4 flex items-center justify-center w-full mt-5"
            >
              Submit
            </button>
          </div>
        </form>
      }
    </div>
  )
}

export default CreateLoadingScheduleForm